import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface StarRatingProps {
  value?: number | null;
  onChange?: (value: number | undefined) => void;
  size?: "sm" | "md";
  readOnly?: boolean;
  className?: string;
  ariaLabel?: string;
}

/**
 * 1–5 star rating control. When `readOnly`, renders a compact display.
 * Click on the currently-selected star to clear the rating.
 */
const StarRating = ({
  value,
  onChange,
  size = "sm",
  readOnly = false,
  className,
  ariaLabel = "Rating",
}: StarRatingProps) => {
  const px = size === "sm" ? "w-3.5 h-3.5" : "w-5 h-5";
  const stars = [1, 2, 3, 4, 5];
  const current = value ?? 0;

  if (readOnly && !current) return null;

  return (
    <div
      className={cn("inline-flex items-center gap-0.5", className)}
      role={readOnly ? "img" : "radiogroup"}
      aria-label={readOnly ? `${ariaLabel}: ${current} of 5` : ariaLabel}
    >
      {stars.map((n) => {
        const filled = n <= current;
        const Cmp = readOnly ? "span" : "button";
        return (
          <Cmp
            key={n}
            {...(readOnly
              ? {}
              : {
                  type: "button" as const,
                  role: "radio",
                  "aria-checked": filled,
                  "aria-label": `${n} star${n > 1 ? "s" : ""}`,
                  onClick: () => onChange?.(current === n ? undefined : n),
                  className:
                    "p-0.5 -m-0.5 touch-manipulation focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-300 rounded",
                })}
          >
            <Star
              className={cn(
                px,
                "transition-colors",
                filled ? "text-amber-300 fill-amber-300" : "text-slate-500"
              )}
            />
          </Cmp>
        );
      })}
    </div>
  );
};

export default StarRating;
